import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import { AnalyticsData, QuestionStatus } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const statusColors: Record<QuestionStatus, string> = {
  pending: 'bg-yellow-500',
  approved: 'bg-blue-800',
  answered: 'bg-green-600',
  rejected: 'bg-red-500',
};

export default function AdminDashboardPage() {
  const { getAccessToken } = useAuth();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Unable to authenticate');
        return;
      }

      const response = await adminService.getAnalytics(token);
      if (response.success && response.data) {
        setAnalytics(response.data);
        setError(null);
      } else {
        setError(response.error || 'Failed to fetch analytics');
      }
    } catch (err) {
      setError('Failed to fetch analytics');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = await getAccessToken();
      if (!token) {
        alert('Unable to authenticate');
        return;
      }

      const blob = await adminService.exportToExcel(token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AMA_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const totalByStatus = Object.values(analytics.questionsByStatus).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">Overview of AMA activity and analytics</p>
        </div>
        <button
          onClick={handleExport}
          disabled={isExporting}
          className="btn-primary flex items-center gap-2"
        >
          {isExporting ? (
            <>
              <LoadingSpinner size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export to Excel
            </>
          )}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-blue-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-blue-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Questions</p>
                <p className="text-2xl font-semibold text-gray-900">{analytics.totalQuestions}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 15l7-7 7 7"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Votes</p>
                <p className="text-2xl font-semibold text-gray-900">{analytics.totalVotes}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-yellow-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Review</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics.questionsByStatus.pending}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Answered</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {analytics.questionsByStatus.answered}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-gray-900">Questions by Status</h2>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {(Object.entries(analytics.questionsByStatus) as [QuestionStatus, number][]).map(
              ([status, count]) => (
                <div key={status} className="flex items-center">
                  <span className="w-24 text-sm font-medium text-gray-600 capitalize">{status}</span>
                  <div className="flex-1 mx-4">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${statusColors[status]} transition-all duration-500`}
                        style={{
                          width: totalByStatus > 0 ? `${(count / totalByStatus) * 100}%` : '0%',
                        }}
                      />
                    </div>
                  </div>
                  <span className="w-12 text-sm text-gray-600 text-right">{count}</span>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Top Voted Questions */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Top Voted Questions</h2>
          <Link to="/admin/questions" className="text-sm text-blue-800 hover:text-blue-900">
            View all
          </Link>
        </div>
        <div className="divide-y divide-gray-200">
          {analytics.topVotedQuestions.slice(0, 5).map((question) => (
            <div key={question.id} className="px-6 py-4 flex items-center gap-4">
              <div className="flex-shrink-0 w-12 text-center">
                <span className="text-lg font-semibold text-blue-800">{question.voteCount}</span>
                <span className="block text-xs text-gray-500">votes</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{question.questionText}</p>
                <p className="text-xs text-gray-500">
                  by {question.createdByName} &middot;{' '}
                  {new Date(question.createdDate).toLocaleDateString()}
                </p>
              </div>
              <span
                className={`badge ${
                  question.status === 'pending'
                    ? 'badge-pending'
                    : question.status === 'approved'
                    ? 'badge-approved'
                    : question.status === 'answered'
                    ? 'badge-answered'
                    : 'badge-rejected'
                }`}
              >
                {question.status}
              </span>
            </div>
          ))}
          {analytics.topVotedQuestions.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">No questions yet</div>
          )}
        </div>
      </div>

      {/* Recent Questions */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Recent Questions</h2>
          <Link to="/admin/questions" className="text-sm text-blue-800 hover:text-blue-900">
            View all
          </Link>
        </div>
        <div className="divide-y divide-gray-200">
          {analytics.recentQuestions.slice(0, 5).map((question) => (
            <div key={question.id} className="px-6 py-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{question.questionText}</p>
                <p className="text-xs text-gray-500">
                  by {question.createdByName} &middot;{' '}
                  {new Date(question.createdDate).toLocaleDateString()}
                </p>
              </div>
              <span className="text-sm text-gray-500">{question.voteCount} votes</span>
              <span
                className={`badge ${
                  question.status === 'pending'
                    ? 'badge-pending'
                    : question.status === 'approved'
                    ? 'badge-approved'
                    : question.status === 'answered'
                    ? 'badge-answered'
                    : 'badge-rejected'
                }`}
              >
                {question.status}
              </span>
            </div>
          ))}
          {analytics.recentQuestions.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">No questions yet</div>
          )}
        </div>
      </div>
    </div>
  );
}
